import React, { useMemo, useState } from 'react';
import useAnalytics from 'helpers/useAnalytics';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import TruncateItemWithTooltip from 'helpers/truncateItemWithTooltip';
import { useHistory } from 'react-router';
import { Theme } from 'styles/ThemeTyping';
import * as Types from 'pages/RolesAndPermissions/typings';
import { ROLES_TABLE_ROWS_PER_PAGE_OPTIONS } from 'pages/RolesAndPermissions/consts';
import {
  HOME_URL,
  ROLE_CREATE_URL,
  ROLES_AND_PERMISSIONS_URL,
  ROLES_URL,
} from 'components/Router/consts';

import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import Table from 'components/Table';
import TableHead from 'components/Table/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import TablePagination from '@material-ui/core/TablePagination';
import TableHeadCell from 'components/Table/TableHeadCell';

interface Props {
  roles: Types.Role[];
}

function RolesTab({ roles }: Props) {
  const classes = useStyles();
  const analytics = useAnalytics();
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  const handleCreateRole = () => {
    analytics.track('Roles/Permissions - Create Role Clicked', {
      category: 'AD - Roles/Permissions',
    });

    history.push(`${HOME_URL}${ROLES_AND_PERMISSIONS_URL}${ROLE_CREATE_URL}`);
  };

  const handleEditRole = (id: string) => {
    history.push(`${HOME_URL}${ROLES_AND_PERMISSIONS_URL}${ROLES_URL}/${id}`);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const tableColumns = useMemo(
    () => [
      { field: 'name', headerName: 'Name', width: '20%' },
      { field: 'description', headerName: 'Description', width: '70%' },
      { field: 'origin', headerName: 'Origin', width: '10%' },
    ],
    [],
  );

  const columnWidths = useMemo(() => {
    const map: Record<string, number | string> = {};
    tableColumns.forEach(col => {
      map[col.field] = col.width !== undefined ? col.width : 'auto';
    });
    return map;
  }, [tableColumns]);

  return (
    <Paper className={classes.root}>
      <div className={classes.tableHeader}>
        <Typography variant="h5">Roles</Typography>
        <Button variant="contained" color="secondary" onClick={handleCreateRole}>
          Create Role
        </Button>
      </div>
      <div className={classes.table}>
        <Table>
          <colgroup>
            {tableColumns.map(col => (
              <col key={col.field} style={{ width: columnWidths[col.field] }} />
            ))}
          </colgroup>

          <TableHead>
            {tableColumns.map(column => (
              <TableHeadCell key={column.field} className={classes.tableHeaderCell}>
                {column.headerName}
              </TableHeadCell>
            ))}
          </TableHead>
          <TableBody>
            {roles
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((role: Types.Role) => (
                <TableRow
                  key={role.name}
                  className={classes.row}
                  onClick={() => handleEditRole(role.id)}
                >
                  <TableCell className={classNames(classes.cell, classes.nameCell)}>
                    <TruncateItemWithTooltip option={role.name} />
                  </TableCell>
                  <TableCell className={classes.cell}>{role.description}</TableCell>
                  <TableCell className={classes.cell}>
                    {role.isDefault ? (
                      <div className={classes.origin}>
                        <FiberManualRecordIcon
                          className={classes.originIndicator}
                          color="secondary"
                        />
                        Default
                      </div>
                    ) : (
                      <div className={classes.origin}>
                        <FiberManualRecordIcon className={classes.originIndicator} color="error" />
                        Custom
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          rowsPerPageOptions={ROLES_TABLE_ROWS_PER_PAGE_OPTIONS}
          count={Number(roles.length)}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </div>
    </Paper>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    padding: 24,
  },
  table: {
    marginTop: theme.spacing(3),
    maxWidth: 1400,
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableHeaderCell: {
    paddingLeft: 0,
    fontWeight: theme.typography.fontWeightSemiBold,
  },
  row: {
    height: 53,
    cursor: 'pointer',
  },
  cell: {
    paddingLeft: 0,
  },
  nameCell: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  origin: {
    display: 'flex',
    alignItems: 'center',
  },
  originHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  originIndicator: {
    maxHeight: 8,
    maxWidth: 8,
    marginRight: theme.spacing(1),
  },
  infoIcon: {
    maxHeight: 18,
    maxWidth: 18,
    marginLeft: theme.spacing(),
  },
}));

export default RolesTab;
